import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Siren,
  Timer,
  Users,
  EyeOff,
  PhoneIncoming,
  MessageCircle,
  Navigation,
  LifeBuoy,
  Languages,
  AudioLines,
  Plus,
} from "lucide-react";
import { RevealText } from "./RevealText";
import { IMAGES } from "../../lib/landing";

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0 },
};

const SOS_CHIPS = ["Scuoti", "Tieni premuto", "Foto", "Audio", "GPS live"];

const SOS_DETAIL =
  "L'SOS è il cuore di Safe(H)er. Puoi attivarlo scuotendo il telefono, con un tocco prolungato o con la voce. Una volta attivo, invia in modo silenzioso la tua posizione GPS live, scatta una foto dalla fotocamera e avvia la registrazione audio dell'ambiente, allertando all'istante il tuo Circle e, dove disponibile, i servizi di emergenza. Tutto continua a funzionare anche a schermo bloccato.";

const CARDS = [
  {
    icon: AudioLines,
    title: "Scream activation",
    body: "Attiva l'SOS con la tua voce: se gridi, l'allarme parte automaticamente.",
    detail:
      "Safe(H)er ascolta in background e riconosce un urlo di aiuto: quando lo rileva, fa partire l'allarme SOS senza bisogno di toccare lo schermo. È pensato per i momenti in cui hai le mani occupate, il telefono in tasca o non riesci a reagire in tempo. Il riconoscimento avviene sul dispositivo, nel rispetto della tua privacy.",
    testid: "feature-scream",
  },
  {
    icon: Timer,
    title: "Guardian Angel timer",
    body: "Imposta un timer per un tragitto: se non confermi di essere al sicuro, l'allarme parte da solo.",
    detail:
      "Prima di metterti in cammino imposti una durata. Safe(H)er ti chiede di confermare che sei al sicuro allo scadere del tempo: se non rispondi, avvisa automaticamente il tuo Circle con la tua posizione. Ideale per rientri notturni, appuntamenti al buio o percorsi che non conosci.",
    testid: "feature-guardian",
  },
  {
    icon: Users,
    title: "Circle",
    body: "La tua cerchia fidata senza numeri di telefono: solo un ID privato, privacy al 100%.",
    detail:
      "Aggiungi le persone fidate tramite un ID privato: niente rubrica, nessun numero di telefono condiviso. Ogni membro del Circle riceve i tuoi allarmi e può vedere la tua posizione solo durante un'emergenza. Privacy totale, controllo totale.",
    testid: "feature-circle",
  },
  {
    icon: EyeOff,
    title: "Camouflage",
    body: "Rendi l'app invisibile: si nasconde dietro un'altra interfaccia per non esporti a rischi.",
    detail:
      "Con un gesto l'app assume l'aspetto di un'altra applicazione — ad esempio una calcolatrice o un meteo. In situazioni di controllo o pericolo nessuno può capire che stai usando uno strumento di sicurezza. La protezione resta attiva, ma nascosta.",
    testid: "feature-camouflage",
  },
  {
    icon: PhoneIncoming,
    title: "Finta chiamata",
    body: "Simula una chiamata in arrivo per uscire da una situazione scomoda in modo naturale.",
    detail:
      "Programma o attiva al volo una chiamata in arrivo realistica, con nome e suoneria personalizzabili. Ti offre una scusa credibile per interrompere un'interazione scomoda e allontanarti in sicurezza, senza destare sospetti.",
    testid: "feature-fakecall",
  },
  {
    icon: MessageCircle,
    title: "Alert via SMS & WhatsApp",
    body: "Messaggi d'allarme automatici inviati via SMS e WhatsApp ai tuoi contatti fidati.",
    detail:
      "Quando scatta l'allarme, i tuoi contatti ricevono un messaggio predefinito con la tua posizione sia via SMS sia via WhatsApp. Così l'avviso arriva anche senza connessione dati o se il destinatario non ha l'app installata.",
    testid: "feature-messaging",
  },
  {
    icon: Navigation,
    title: "Posizione live & GPS",
    body: "Condividi il percorso in tempo reale con posizione GPS precisa fino a destinazione.",
    detail:
      "Condividi in tempo reale il tuo percorso e la posizione GPS precisa fino all'arrivo. Il tuo Circle può seguirti sulla mappa e la condivisione si interrompe automaticamente una volta raggiunta la destinazione.",
    testid: "feature-geo",
  },
  {
    icon: LifeBuoy,
    title: "Aiuto vicino a te",
    body: "In base alla posizione: numeri di soccorso, supporto e associazioni sul territorio.",
    detail:
      "In base alla tua posizione, Safe(H)er mostra numeri di soccorso, centri di supporto e associazioni presenti sul territorio, con contatto diretto in un tocco. L'aiuto giusto, dove ti trovi, quando serve.",
    testid: "feature-resources",
  },
  {
    icon: Languages,
    title: "Lingua automatica",
    body: "L'app rileva la lingua in base alla posizione del dispositivo, ovunque tu sia.",
    detail:
      "L'app rileva la lingua in base alla posizione del dispositivo e adatta interfaccia e messaggi di emergenza di conseguenza. Utile in viaggio o all'estero, per comunicare con i soccorsi nella lingua giusta.",
    testid: "feature-language",
  },
];

const SmallCard = ({ card, isOpen, onToggle, delay }) => {
  const { icon: Icon, title, body, detail, testid } = card;
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      variants={cardVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      aria-expanded={isOpen}
      data-testid={testid}
      className={`md:col-span-2 text-left relative overflow-hidden rounded-3xl border p-8 transition-colors duration-300 ${
        isOpen
          ? "border-brand-primary bg-brand-bg"
          : "border-brand-line bg-brand-bg hover:border-brand-primary/50"
      }`}
    >
      <div className="flex items-start justify-between">
        <Icon size={24} className="text-brand-primary" />
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-full border border-brand-line text-brand-muted transition-transform duration-300 ${
            isOpen ? "rotate-45" : ""
          }`}
        >
          <Plus size={16} />
        </span>
      </div>
      <h3 className="mt-6 font-display text-2xl text-brand-text tracking-tight">
        {title}
      </h3>
      <p className="mt-3 text-brand-muted leading-relaxed text-sm">{body}</p>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p
              className="mt-4 pt-4 border-t border-brand-line text-brand-muted/90 leading-relaxed text-sm"
              data-testid={`${testid}-detail`}
            >
              {detail}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export const Features = () => {
  const [open, setOpen] = useState(null);
  const toggle = (id) => setOpen((prev) => (prev === id ? null : id));
  const sosOpen = open === "sos";

  return (
    <section
      id="funzioni"
      data-testid="features-section"
      className="relative py-28 sm:py-36 bg-brand-surface"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-6">
          <h2 className="font-display font-medium tracking-tight leading-[0.95] text-brand-text text-4xl sm:text-5xl md:text-6xl">
            <RevealText inView lines={["Protezione", "in un solo gesto."]} />
          </h2>
          <p className="max-w-sm text-brand-muted text-base md:text-lg leading-relaxed">
            Pensata per la protezione delle donne, ma può aiutare chiunque. Ogni
            funzione dà controllo, calma e reazione immediata nei momenti che
            contano.
          </p>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-brand-primary mb-10">
          Tocca una funzione per scoprire di più
        </p>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-start">
          {/* Dominant SOS card */}
          <motion.button
            type="button"
            onClick={() => toggle("sos")}
            variants={cardVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            aria-expanded={sosOpen}
            data-testid="feature-sos"
            className="md:col-span-6 text-left relative overflow-hidden rounded-3xl border border-brand-line bg-brand-dark min-h-[340px] group"
          >
            <img
              src={IMAGES.texture}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-luminosity transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-dark via-brand-dark/80 to-brand-primary/40" />
            <div className="relative h-full p-8 sm:p-10 flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-primary text-brand-inverse">
                  <Siren size={26} />
                </div>
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full border border-brand-inverse/25 text-brand-inverse transition-transform duration-300 ${
                    sosOpen ? "rotate-45" : ""
                  }`}
                >
                  <Plus size={18} />
                </span>
              </div>
              <div>
                <h3 className="font-display text-3xl sm:text-4xl text-brand-inverse tracking-tight">
                  Allarme SOS istantaneo
                </h3>
                <p className="mt-4 text-brand-inverse/70 max-w-md leading-relaxed">
                  Attivalo scuotendo il telefono, urlando o con un tocco
                  prolungato. Invia la posizione GPS live, foto e audio ai tuoi
                  contatti fidati e ai servizi di emergenza.
                </p>
                <AnimatePresence initial={false}>
                  {sosOpen && (
                    <motion.p
                      key="sos-detail"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="mt-4 text-brand-inverse/60 max-w-lg leading-relaxed text-sm overflow-hidden"
                      data-testid="feature-sos-detail"
                    >
                      {SOS_DETAIL}
                    </motion.p>
                  )}
                </AnimatePresence>
                <div className="mt-6 flex flex-wrap gap-2">
                  {SOS_CHIPS.map((c) => (
                    <span
                      key={c}
                      className="rounded-full border border-brand-inverse/20 text-brand-inverse/80 text-xs px-3 py-1.5"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.button>

          {CARDS.map((card, i) => (
            <SmallCard
              key={card.testid}
              card={card}
              isOpen={open === card.testid}
              onToggle={() => toggle(card.testid)}
              delay={0.05 + (i % 2) * 0.05}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
